import { useEffect, useState } from 'react';
import { useAppSelector, useAppDispatch } from 'flow/hooks/useRedux';
import { fromJS } from 'immutable';

import { updateProfiles } from 'flow/actions/profile-actions';
import { selectHasCapability } from 'flow/selectors/ExtensionSelectors';
import Icon from 'flow/components/ui-components/Icon';
import Loading from 'flow/components/ui-components/Loading';
import RouteLink from 'flow/components/ui-components/RouteLink';
import Toggle from 'flow/components/ui-components/Toggle';
import { useModal } from 'flow/contexts/ModalProvider';
import useAppContext from 'flow/hooks/useAppContext';
import useContact from 'flow/hooks/useContact';
import useProfiles from 'flow/hooks/useProfiles';
import useSettingsNavigation from 'flow/hooks/useSettingsNavigation';
import useSinglePaneMode from 'flow/hooks/useSinglePaneMode';
import { Profile } from 'flow/types/Profile';
import { routes } from 'flow/types/routes';

import { TypedMap } from 'flow/types/TypedMap';
import {
    callerIdLandlineVaries,
    callerIdMobileVaries,
    callerIdTextMessageVaries,
    callerIdVaries,
    canEditProfiles,
    getProfileCallerId,
    hasLandlineCallerId,
    updateFixedProfile,
    updateMobileProfile,
} from 'flow/utils/profile-utils';
import { generatePath } from 'flow/utils/router-utils';
import Page from './Page';
import { ExtensionKey } from 'flow/types/EntityKeys';
import { useParams } from 'react-router-dom';
import { Capabilities } from 'flow/utils/capability-utils';

const CallerId = () => {
    const { i18next } = useAppContext();
    const { openModal } = useModal();

    const { extensionKey } = useParams<{ extensionKey: ExtensionKey }>();
    const { profiles } = useProfiles(extensionKey);
    const { contact } = useContact(extensionKey);

    const hasCapabilitySms = useAppSelector((state) =>
        selectHasCapability(state, extensionKey, Capabilities.SMS),
    );
    const { isSinglePaneMode } = useSinglePaneMode();
    const dispatch = useAppDispatch();
    const { goToSettings } = useSettingsNavigation();

    const [varies, setVaries] = useState(callerIdVaries(profiles));
    const [editByProfile, setEditByProfile] = useState(varies);

    useEffect(() => {
        setVaries(callerIdVaries(profiles));
        setEditByProfile(callerIdVaries(profiles));
    }, [profiles]);

    const isTechnicalUser = useAppSelector((state) =>
        state.app.hasIn(['extension', 'contact', 'technicalNumber']),
    );

    if (!profiles?.size || !contact) {
        return <Loading signalFinishedLoading={true} />;
    }

    const optionList = profiles.get(0).getIn(['callerId', 'callerIdOptions']);

    const showLandline =
        (optionList.includes('fixed') || isTechnicalUser) &&
        hasLandlineCallerId(profiles.first());

    const concatCallerId = (
        profile1: TypedMap<Profile>,
        profile2: TypedMap<Profile>,
    ) =>
        fromJS({
            key: profile1.get('key'),
            callerId: profile1.get('callerId').concat(profile2.get('callerId')),
        });

    const prepareCallerID = (
        profile: TypedMap<Profile>,
        profileCallerIdType: string,
        mainNumber: string,
    ) => {
        const profileCallerId = profile.getIn([
            'callerId',
            profileCallerIdType,
        ]);

        const callerIdTypes = ['fixed', 'msisdn', 'hidden'];

        return callerIdTypes.includes(profileCallerId)
            ? profileCallerId
            : profile.getIn(['callerId', mainNumber, 'key']);
    };

    const resetProfiles = () => {
        let updatedProfileList = profiles;

        const updateProfileList = (
            updateProfileFunc: Function,
            callerIdType: string,
            mainNumber: string,
        ) =>
            updatedProfileList.map((profile) =>
                updateProfileFunc(
                    profile,
                    prepareCallerID(profiles.first(), callerIdType, mainNumber),
                ),
            );

        if (optionList.includes('fixed') || isTechnicalUser) {
            updatedProfileList = updateProfileList(
                updateFixedProfile,
                'fixedCallerIdType',
                'fixedMainNumber',
            );
        }

        if (optionList.includes('msisdn')) {
            const msisdnList = updateProfileList(
                updateMobileProfile,
                'mexCallerIdType',
                'mexMainNumber',
            );

            updatedProfileList = updatedProfileList.map((profile) => {
                const updatedMobileProfile = msisdnList.find(
                    (profile2) => profile.get('key') === profile2.get('key'),
                );
                return concatCallerId(profile, updatedMobileProfile);
            });
        }

        const filteredBySMSSenderNameId = profiles.filter(
            (profile) =>
                profile.getIn(['smsSenderNameId', 'key']) ===
                profiles.first().getIn(['smsSenderNameId', 'key']),
        );

        if (filteredBySMSSenderNameId.size !== profiles.size) {
            const refKey = profiles.first().getIn(['smsSenderNameId', 'key']);
            updatedProfileList = updatedProfileList.map((profile) =>
                profile.setIn(['smsSenderNameId', 'key'], refKey),
            );
        }

        dispatch(updateProfiles(extensionKey, updatedProfileList));

        setVaries(false);
        setEditByProfile(false);
    };

    const toggleProfileHandler = (value: boolean) => {
        if (varies && !value) {
            openModal('confirmationDialog', {
                title: i18next.t('settings.reset.warning.general.Title', {
                    currentSettings: i18next.t('settings.callerid'),
                }),
                description: (
                    <p
                        dangerouslySetInnerHTML={{
                            __html: i18next.t(
                                'settings.reset.warning.general',
                                {
                                    currentSettings:
                                        i18next.t('settings.callerid'),
                                    firstProfileName:
                                        !profiles.isEmpty() &&
                                        profiles.first().get('description'),
                                },
                            ),
                        }}
                    />
                ),
                handleAccept: resetProfiles,
                confirmButtonText: i18next.t('navigate.save'),
            });
        } else {
            setEditByProfile(value);
        }
    };

    return (
        <Page
            header="settings.callerid"
            onClickBack={isSinglePaneMode ? goToSettings : undefined}
            footer={
                canEditProfiles(profiles) ? (
                    <>
                        <p>{i18next.t('settings.profile_edit')}</p>
                        <Toggle
                            value={editByProfile}
                            onChange={toggleProfileHandler}
                            text={i18next.t('settings.profile_edit')}
                        />
                    </>
                ) : null
            }
        >
            <div className="flex flex-col divide-y">
                {showLandline && (
                    <div className="pb-6">
                        <RouteLink
                            to={generatePath(
                                editByProfile
                                    ? routes[
                                          'settings.callerid.profile.numberType'
                                      ]
                                    : routes[
                                          'settings.callerid.edit.numberType'
                                      ],
                                {
                                    extensionKey,
                                    numberType: 'fixed',
                                },
                            )}
                            className="group"
                            description={
                                callerIdLandlineVaries(profiles)
                                    ? i18next.t('settings.varies_per_profile')
                                    : getProfileCallerId(
                                          i18next,
                                          profiles.first(),
                                          contact,
                                          'fixedCallerIdType',
                                          'fixedMainNumber',
                                      )
                            }
                        >
                            <div className="rounded-lg bg-gray-100 p-1.5 group-hover:bg-gray-200">
                                <Icon iconKey="phone" />
                            </div>
                            {i18next.t('settings.landline')}
                        </RouteLink>
                        <p className="p-2 text-xs text-gray-500">
                            {i18next.t('settings.number_present_fixed')}
                        </p>
                    </div>
                )}

                {optionList.includes('msisdn') && (
                    <div className="pb-6">
                        <RouteLink
                            to={generatePath(
                                editByProfile
                                    ? routes[
                                          'settings.callerid.profile.numberType'
                                      ]
                                    : routes[
                                          'settings.callerid.edit.numberType'
                                      ],
                                {
                                    extensionKey,
                                    numberType: 'msisdn',
                                },
                            )}
                            className="group"
                            description={
                                callerIdMobileVaries(profiles)
                                    ? i18next.t('settings.varies_per_profile')
                                    : getProfileCallerId(
                                          i18next,
                                          profiles.first(),
                                          contact,
                                          'mexCallerIdType',
                                          'mexMainNumber',
                                      )
                            }
                        >
                            <div className="rounded-lg bg-gray-100 p-1.5 group-hover:bg-gray-200">
                                <Icon iconKey="mobile" />
                            </div>
                            {i18next.t('settings.mobile')}
                        </RouteLink>
                        <p className="p-2 text-xs text-gray-500">
                            {i18next.t('settings.number_present_mobile')}
                        </p>
                    </div>
                )}

                {hasCapabilitySms && profiles.first().get('smsSenderNameId') ? (
                    <div>
                        <RouteLink
                            to={
                                editByProfile
                                    ? generatePath(
                                          routes[
                                              'settings.callerid.profile.sms'
                                          ],
                                          { extensionKey },
                                      )
                                    : generatePath(
                                          routes[
                                              'settings.callerid.edit.numberType'
                                          ],
                                          {
                                              extensionKey,
                                              numberType: 'sms',
                                          },
                                      )
                            }
                            className="group"
                            description={
                                callerIdTextMessageVaries(profiles)
                                    ? i18next.t('settings.varies_per_profile')
                                    : profiles
                                          .first()
                                          .getIn([
                                              'smsSenderNameId',
                                              'description',
                                          ])
                            }
                        >
                            <div className="rounded-lg bg-gray-100 p-1.5 group-hover:bg-gray-200">
                                <Icon iconKey="mobile" />
                            </div>
                            {i18next.t('settings.callerid.sms.title')}
                        </RouteLink>
                        <p className="p-2 text-xs text-gray-500">
                            {i18next.t('settings.callerid.sms.description')}
                        </p>
                    </div>
                ) : null}
            </div>
        </Page>
    );
};

export default CallerId;
